/**
 * The project's pause: an operational stop for every dispatch, while the
 * definitions stay enabled. Any Developer may pause (`can_pause`); resuming
 * also needs eligibility for the agent-runs features (`can_resume`). While paused, a prominent banner
 * names who paused (their GitLab username) and when.
 */

import type { ReactElement } from 'react';
import { useSetPaused } from '../hooks/useProjectPanelMutations';
import { usePanelSwitch } from '../hooks/usePanelSwitch';
import { formatAgo } from '../utils/time';
import { panelErrorMessage } from '../utils/errors';
import type { PanelResponse } from '../types/projectPanel.types';

interface PauseControlProps {
  projectId: string;
  panel: Pick<PanelResponse, 'paused' | 'paused_by' | 'paused_at' | 'can_pause' | 'can_resume'>;
}

function pausedLine(panel: PauseControlProps['panel']): string {
  const by = panel.paused_by === null ? '' : ` by ${panel.paused_by}`;
  const when = panel.paused_at === null ? '' : ` ${formatAgo(panel.paused_at)}`;
  return `Runs paused${by}${when} — no pipeline dispatches until resumed`;
}

export function PauseControl({ projectId, panel }: PauseControlProps): ReactElement | null {
  const { send, isPending, error } = usePanelSwitch(projectId, useSetPaused(projectId));
  const failure = error !== null && (
    <div className="error-message" role="alert">{panelErrorMessage(error)}</div>
  );

  if (panel.paused) {
    return (
      <div className="pause-banner" role="status">
        <strong className="pause-banner__text">{pausedLine(panel)}</strong>
        {panel.can_resume && (
          <button
            type="button"
            className="secondary-button"
            disabled={isPending}
            onClick={() => { send(false); }}
          >
            Resume runs
          </button>
        )}
        {failure}
      </div>
    );
  }

  if (!panel.can_pause) return null;
  return (
    <div className="pause-control">
      <button
        type="button"
        className="secondary-button secondary-button--danger"
        disabled={isPending}
        onClick={() => { send(true); }}
      >
        Pause runs
      </button>
      <span className="pause-control__hint">Stops every dispatch; definitions stay enabled.</span>
      {failure}
    </div>
  );
}
